import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Users, 
  Shield, 
  UserCheck, 
  Plus, 
  MoreHorizontal,
  Settings,
  Mail,
  Calendar
} from 'lucide-react';
import { UserRole } from '@/types';

export const UserManagement: React.FC = () => {
  const { user } = useAuth();

  // Mock users data
  const mockUsers = [
    {
      id: '1',
      name: 'Admin User',
      email: 'admin@example.com',
      role: UserRole.ADMIN,
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face',
      createdAt: new Date('2024-01-01'),
      lastLogin: new Date('2024-01-15'),
      projectsCount: 5,
      tasksCount: 12,
      status: 'active'
    },
    {
      id: '2',
      name: 'Manager User',
      email: 'manager@example.com',
      role: UserRole.MANAGER,
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b865?w=400&h=400&fit=crop&crop=face',
      createdAt: new Date('2024-01-02'),
      lastLogin: new Date('2024-01-14'),
      projectsCount: 3,
      tasksCount: 8,
      status: 'active'
    },
    {
      id: '3',
      name: 'Collaborator User',
      email: 'collaborator@example.com',
      role: UserRole.COLLABORATOR,
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face',
      createdAt: new Date('2024-01-03'),
      lastLogin: new Date('2024-01-13'),
      projectsCount: 1,
      tasksCount: 15,
      status: 'active'
    },
    {
      id: '4',
      name: 'Designer User',
      email: 'designer@example.com',
      role: UserRole.COLLABORATOR,
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face',
      createdAt: new Date('2024-01-05'),
      lastLogin: new Date('2024-01-10'),
      projectsCount: 2,
      tasksCount: 6,
      status: 'inactive'
    }
  ];

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN: return <Shield className="w-4 h-4 text-red-500" />;
      case UserRole.MANAGER: return <UserCheck className="w-4 h-4 text-blue-500" />;
      case UserRole.COLLABORATOR: return <Users className="w-4 h-4 text-green-500" />;
      default: return <Users className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN: return 'bg-red-100 text-red-800';
      case UserRole.MANAGER: return 'bg-blue-100 text-blue-800';
      case UserRole.COLLABORATOR: return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleText = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN: return 'Administrador';
      case UserRole.MANAGER: return 'Gerente';
      case UserRole.COLLABORATOR: return 'Colaborador';
      default: return role;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Ativo';
      case 'inactive': return 'Inativo';
      case 'pending': return 'Pendente';
      default: return status;
    }
  };

  // Check if current user has admin permissions
  if (user?.role !== UserRole.ADMIN) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Acesso Restrito</h3>
          <p className="text-gray-600">Você não tem permissão para acessar o gerenciamento de usuários</p>
        </div>
      </div>
    );
  }

  const activeUsers = mockUsers.filter(u => u.status === 'active').length;
  const totalProjects = mockUsers.reduce((sum, u) => sum + u.projectsCount, 0);
  const totalTasks = mockUsers.reduce((sum, u) => sum + u.tasksCount, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gerenciamento de Usuários</h1>
          <p className="text-gray-600 mt-1">Gerencie usuários e suas permissões</p>
        </div>
        <Button className="bg-gradient-to-r from-blue-500 to-purple-600">
          <Plus className="w-4 h-4 mr-2" />
          Novo Usuário
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total de Usuários</p>
                <p className="text-2xl font-bold text-blue-900">{mockUsers.length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Usuários Ativos</p>
                <p className="text-2xl font-bold text-green-900">{activeUsers}</p>
              </div>
              <UserCheck className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Total de Projetos</p>
                <p className="text-2xl font-bold text-purple-900">{totalProjects}</p>
              </div>
              <Shield className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">Total de Tarefas</p>
                <p className="text-2xl font-bold text-yellow-900">{totalTasks}</p>
              </div>
              <Calendar className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Lista de Usuários
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {mockUsers.map(userData => (
              <div key={userData.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-4">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={userData.avatar} alt={userData.name} />
                    <AvatarFallback>{userData.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="font-medium text-gray-900">{userData.name}</h3>
                      <div className="flex items-center space-x-1">
                        {getRoleIcon(userData.role)}
                        <Badge variant="secondary" className={getRoleColor(userData.role)}>
                          {getRoleText(userData.role)}
                        </Badge>
                      </div>
                      <Badge variant="secondary" className={getStatusColor(userData.status)}>
                        {getStatusText(userData.status)}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center space-x-4 mt-2">
                      <div className="flex items-center space-x-1">
                        <Mail className="w-3 h-3 text-gray-400" />
                        <span className="text-sm text-gray-600">{userData.email}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Último login: {userData.lastLogin.toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  {/* Stats */}
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {userData.projectsCount} projetos
                    </div>
                    <div className="text-sm text-gray-600">
                      {userData.tasksCount} tarefas
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm">
                      <Settings className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Roles and Permissions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Tipos de Usuário e Permissões
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-red-500" />
                <h4 className="font-medium text-gray-900">Administrador</h4>
              </div>
              <ul className="text-sm text-gray-600 space-y-1 pl-7">
                <li>• Acesso total ao sistema</li>
                <li>• Gerenciar usuários</li>
                <li>• Criar/editar/excluir projetos</li>
                <li>• Todas as permissões</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <UserCheck className="w-5 h-5 text-blue-500" />
                <h4 className="font-medium text-gray-900">Gerente</h4>
              </div>
              <ul className="text-sm text-gray-600 space-y-1 pl-7">
                <li>• Criar/editar projetos</li>
                <li>• Gerenciar tarefas</li>
                <li>• Atribuir tarefas</li>
                <li>• Visualizar relatórios</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-green-500" />
                <h4 className="font-medium text-gray-900">Colaborador</h4>
              </div>
              <ul className="text-sm text-gray-600 space-y-1 pl-7">
                <li>• Visualizar projetos atribuídos</li>
                <li>• Editar tarefas próprias</li>
                <li>• Atualizar progresso</li>
                <li>• Comentar em tarefas</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};